import React, { useState, useEffect, useContext } from "react";
import { makeStyles, withStyles } from "@material-ui/core/styles";
import { withOktaAuth } from "@okta/okta-react";
import Dialog from "@material-ui/core/Dialog";
import MuiDialogTitle from "@material-ui/core/DialogTitle";
import MuiDialogContent from "@material-ui/core/DialogContent";
import MuiDialogActions from "@material-ui/core/DialogActions";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import Typography from "@material-ui/core/Typography";
import styles from "./styles";
import { Grid } from "@material-ui/core";
import { withApollo } from "react-apollo";
import { finalizeScore } from "../../../../API/Entity";
import LoadingIndicator from "../../../../Component/LoadingIndicator";
import ModalDialogContext from "../../../../Context/ModalDialog/ModalDialogContext";
import SearchableSelect from "../../../../Component/Forms/SearchableSelect";
import TextField from "../../../../Component/Forms/TextField";
import TextArea from "../../../../Component/Forms/TextArea";
import DateField from "../../../../Component/Forms/DateField";
import { getValidityPeriod } from "../../../../API/Entity";
import { useTranslation } from "react-i18next";
import CheckBox from "../../../../Component/Forms/CheckboxWithLabel";
import CustomButton from "../../../../Component/CustomButton";

const useStyles = makeStyles(styles);

const DialogTitle = withStyles(styles)((props) => {
  const { children, classes, onClose, ...other } = props;
  return (
    <MuiDialogTitle disableTypography className={classes.root} {...other}>
      <Typography variant="h6">{children}</Typography>
      {onClose ? (
        <IconButton
          aria-label="close"
          className={classes.closeButton}
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </MuiDialogTitle>
  );
});

const DialogContent = withStyles((theme) => ({
  root: {
    padding: theme.spacing(4),
  },
}))(MuiDialogContent);

const DialogActions = withStyles((theme) => ({
  root: {
    margin: 0,
    padding: theme.spacing(1),
  },
}))(MuiDialogActions);

const FinalizeScore = ({
  open,
  scoreData,
  checkRequest,
  onClose,
  canFinalizeAMLWithRisk,
}) => {
  const classes = useStyles();
  const [isOpen, setIsOpen] = useState(open);
  const [guidedScore, setGuidedScore] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setCreating] = useState(false);
  const [userScore, setUserScore] = useState(null);
  const [scoreOptions, setScoreOptions] = useState([]);
  const [userComment, setUserComment] = useState("");

  const [guidedValidFrom, setGuidedValidFrom] = useState();
  const [guidedValidTo, setGuidedValidTo] = useState();
  const [guidedValidToWithRisk, setGuidedValidToWithRisk] = useState();
  const [userValidFrom, setUserValidFrom] = useState();
  const [userValidTo, setUserValidTo] = useState();

  const [hasError, setHasError] = useState(false);
  const [scoreError, setScoreError] = useState(false);
  const [commentError, setCommentError] = useState(false);
  const [validFromError, setValidFromError] = useState(false);
  const [validToError, setValidToError] = useState(false);
  const [proceedAtRisk, setProceedAtRisk] = useState(false);

  const { showMessage, showErrorMessage } = useContext(ModalDialogContext);
  const { t } = useTranslation();

  useEffect(() => {
    setScoreOptions([
      { value: "High", label: t("labels.check.scoring.high") },
      { value: "Medium", label: t("labels.check.scoring.medium") },
      { value: "Low", label: t("labels.check.scoring.low") },
    ]);
    let score =
      scoreData && checkRequest ? scoreData[checkRequest.check_type] : "";
    if (score) {
      setGuidedScore(score.guided_score);
      if (score.accepted_score) {
        setUserScore({
          value: score.accepted_score,
          label: score.accepted_score,
        });
      }
    }

    const loadValidity = async () => {
      const result = await getValidityPeriod(
        checkRequest.entity_id,
        checkRequest.check_type,
      );

      const { data = {} } = result;
      const { validFrom, validTo, validToWithRisk } = data;
      setGuidedValidFrom(new Date(validFrom));
      setGuidedValidTo(new Date(validTo));
      setGuidedValidToWithRisk(new Date(validToWithRisk));
      setIsLoading(false);
    };

    loadValidity();

    setIsOpen(open);
  }, [open]);

  useEffect(() => {
    setHasError(false);
    if (
      (userScore && userScore.value !== guidedScore && userComment === "") ||
      (proceedAtRisk && userComment === "")
    ) {
      setHasError(true);
      setCommentError(t("messages.check.finalization.userCommentError"));
    } else {
      setCommentError(false);
    }

    setValidFromError(false);
    setValidToError(false);
    if (userValidFrom) {
      if (userValidFrom.getTime() < guidedValidFrom.getTime()) {
        setHasError(true);
        setValidFromError();
      }
      if (
        (userValidTo && userValidFrom.getTime() > userValidTo.getTime()) ||
        userValidFrom.getTime() > guidedValidTo.getTime()
      ) {
        setHasError(true);
        setValidFromError(t("messages.check.finalization.validFromError"));
      }
    }
    if (userValidTo) {
      if (userValidTo.getTime() > guidedValidTo.getTime()) {
        setHasError(true);
        setValidToError(t("messages.check.finalization.expiryDateError"));
      }
      if (
        (userValidFrom && userValidTo.getTime() < userValidFrom.getTime()) ||
        userValidTo.getTime() < guidedValidFrom.getTime()
      ) {
        setHasError(true);
        setValidToError(
          t("messages.check.finalization.expiryDateLessThanError"),
        );
      }
    }
  });

  const changeUserScore = (score) => {
    setUserScore(score);
    if (score) {
      setScoreError(false);
    }
  };

  const updateUserComment = (v) => {
    setUserComment(v);
  };

  const updateValidFrom = (d) => {
    const fromDate = new Date(d);
    if (isNaN(fromDate.getTime())) {
      return;
    }
    setUserValidFrom(fromDate);
  };

  const updateValidTo = (d) => {
    const toDate = new Date(d);
    if (isNaN(toDate.getTime())) {
      return;
    }
    setUserValidTo(toDate);
  };

  const updateProceedAtRisK = (e) => {
    setProceedAtRisk(e.target.checked);
    setUserValidFrom(guidedValidFrom);
    setUserValidTo(e.target.checked ? guidedValidToWithRisk : guidedValidTo);
  };

  const handleClose = () => {
    setIsOpen(false);
    onClose("cancel");
  };

  const handleSubmit = async () => {
    if (!userScore || !userScore.value) {
      setHasError(true);
      setScoreError(t("messages.check.finalization.userScoreError"));
      return;
    }

    setIsLoading(true);
    setCreating(true);

    try {
      await finalizeScore({
        entityId: checkRequest.entity_id,
        checkType: checkRequest.check_type,
        acceptedScore: userScore ? userScore.value : null,
        guidedScore: guidedScore,
        userComment: userComment,
        userValidFrom,
        userValidTo,
        proceedAtRisk,
      });
      showMessage({ message: t("messages.check.finalization.scoreFinalized") });
      setCreating(false);
      setIsLoading(false);
      setIsOpen(false);
      onClose();
    } catch (error) {
      showErrorMessage({ error });
      setIsLoading(false);
      setCreating(false);
      //setIsOpen(false);
      //onClose();
    }
  };

  const renderLoadingIndicator = () => {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <LoadingIndicator />
      </div>
    );
  };

  const renderNoScore = () => {
    return (
      <span className={classes.errorText}>
        {t("messages.check.finalization.calculateGuidedScore")}
      </span>
    );
  };

  const renderFinalizeForm = () => {
    return (
      <>
        <Grid container direction="row" spacing={4}>
          <Grid item xs={12} md={6} sm={6} style={{ paddingLeft: 15 }}>
            <TextField
              label={t("labels.check.finalization.guidedScore")}
              initialValue={guidedScore}
              type="text"
              disabled={true}
            />
          </Grid>
          <Grid item sm={6} md={6} xs={12}>
            <SearchableSelect
              label={t("labels.check.finalization.userScore")}
              options={scoreOptions}
              value={userScore}
              required={true}
              changeHandler={changeUserScore}
            />
            {scoreError && (
              <span className={classes.errorText}>{scoreError}</span>
            )}
          </Grid>
          <Grid item xs={12} md={6} sm={6} style={{ paddingLeft: 15 }}>
            <DateField
              initialValue={guidedValidFrom}
              label={t("labels.check.finalization.guidedValidFromDate")}
              disabled={true}
            />
          </Grid>
          <Grid item xs={12} md={6} sm={6} style={{ paddingLeft: 15 }}>
            <DateField
              initialValue={guidedValidTo}
              label={t("labels.check.finalization.guidedExpiryDate")}
              disabled={true}
            />
          </Grid>
          <Grid item xs={12} md={6} sm={6} style={{ paddingLeft: 15 }}>
            <DateField
              label={t("labels.check.finalization.overrideValidFromDate")}
              isRequired={true}
              initialValue={guidedValidFrom}
              onChange={updateValidFrom}
              disabled={canFinalizeAMLWithRisk && proceedAtRisk}
            />
            {validFromError && (
              <span className={classes.errorText}>{validFromError}</span>
            )}
          </Grid>
          <Grid item xs={12} md={6} sm={6} style={{ paddingLeft: 15 }}>
            <DateField
              label={t("labels.check.finalization.overrideExpiryDate")}
              isRequired={true}
              initialValue={
                proceedAtRisk ? guidedValidToWithRisk : guidedValidTo
              }
              onChange={updateValidTo}
              disabled={canFinalizeAMLWithRisk && proceedAtRisk}
            />
            {validToError && (
              <span className={classes.errorText}>{validToError}</span>
            )}
          </Grid>
        </Grid>
        <Grid container direction="row" spacing={4}>
          <Grid item xs={12} md={6} sm={12} style={{ paddingLeft: 15 }}>
            <TextArea
              label={t("labels.check.finalization.userComment")}
              rowsMax={3}
              placeholder={t("labels.check.finalization.pleaseEnterYouComment")}
              initialValue={userComment}
              onChange={updateUserComment}
            />
            {commentError && (
              <span className={classes.errorText}>{commentError}</span>
            )}
          </Grid>
          {checkRequest.check_type === "AML" && canFinalizeAMLWithRisk && (
            <Grid item xs={12} md={6} sm={12} style={{ paddingLeft: 15 }}>
              <Grid container item xs={12} md={12} sm={12}>
                &nbsp;
              </Grid>
              <Grid container item xs={12} md={12} sm={12}>
                <CheckBox
                  label={t("labels.check.finalization.proceedAtRisk")}
                  onChange={updateProceedAtRisK}
                  value={proceedAtRisk}
                />
              </Grid>
            </Grid>
          )}
        </Grid>
      </>
    );
  };

  return (
    <Dialog
      disableBackdropClick
      onClose={handleClose}
      aria-labelledby="customized-dialog-title"
      open={isOpen}
      fullWidth={true}
      maxWidth="md"
    >
      <DialogTitle id="customized-dialog-title" onClose={handleClose}>
        <Typography className={classes.panelTitle}>
          {t("labels.check.finalization.finalizeScore")}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        {isLoading
          ? renderLoadingIndicator()
          : guidedScore
          ? renderFinalizeForm()
          : renderNoScore()}
      </DialogContent>
      <DialogActions>
          <CustomButton 
            variant="secondary" 
            isDisabled={isCreating}
            onClick={handleClose}
          >
            {t("labels.common.cancel")}
          </CustomButton>
          <CustomButton 
            variant="primary" 
            loadingText={t("labels.check.finalization.finalizing")}
            isLoading={isCreating}
            isDisabled={isCreating || hasError}
            onClick={handleSubmit}
          >
            {t("labels.check.finalization.finalize")}
          </CustomButton>


        
      </DialogActions>
    </Dialog>
  );
};

export default withOktaAuth(withApollo(FinalizeScore));
